/**
 * Preparation d'une photo de plat avant envoi.
 *
 * Un capteur de telephone produit 4 Mo pour une image que la carte affiche sur
 * 320 px de large. La salle est en reseau mobile faible (docs/15, section 1) :
 * redimensionner ici epargne a la fois le reseau du gerant et le stockage.
 *
 * Voir docs/adr/0023-televerser-la-photo-d-un-plat.md.
 */

import { MENU_ITEM_IMAGE_MAX_EDGE_PX } from '@scan2eat/shared';

/** Qualite JPEG. 0,82 : au-dela le poids double sans gain visible a l'ecran. */
const JPEG_QUALITY = 0.82;

/**
 * Dimensions apres reduction, a proportions conservees.
 *
 * Une image deja plus petite que la borne n'est pas agrandie : etirer une
 * photo floue ne la rend pas nette, elle pese seulement plus lourd.
 */
export function fitWithin(
  width: number,
  height: number,
  maxEdge: number,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxEdge || longest === 0) return { width, height };

  const ratio = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
}

/**
 * Rend l'image reduite, en JPEG.
 *
 * En cas d'echec -- format que le navigateur ne sait pas decoder, canevas
 * indisponible -- le fichier d'origine est rendu tel quel : le serveur reste
 * juge du format et de la taille, et mieux vaut un envoi lourd qu'un envoi
 * impossible.
 */
export async function prepareImageForUpload(file: File): Promise<Blob> {
  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = fitWithin(bitmap.width, bitmap.height, MENU_ITEM_IMAGE_MAX_EDGE_PX);

    if (width === bitmap.width && height === bitmap.height && file.size < 900_000) {
      bitmap.close();
      return file;
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
      bitmap.close();
      return file;
    }

    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY);
    });

    return blob ?? file;
  } catch {
    return file;
  }
}
